import { api } from '../../lib/api.ts';

export type DocStatus = 'DRAFT' | 'IN_REVIEW' | 'PENDING' | 'COMPLETED' | 'DECLINED' | 'CANCELLED';

export interface DocRow {
  id: string;
  title: string;
  status: DocStatus;
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  sender: { id: string; name: string; email: string };
}

export type DocTab = 'sent' | 'to-sign' | 'to-review' | 'completed' | 'all';

export interface DocDetail extends DocRow {
  message: string | null;
  reviewerId: string | null;
  reviewRequestedAt: string | null;
  reviewedAt: string | null;
  reviewDecision: 'APPROVED' | 'REJECTED' | null;
  reviewComment: string | null;
  reviewer: { id: string; name: string; email: string } | null;
  placeholders: Array<{
    id: string;
    signerId: string | null;
    kind: 'signature' | 'date' | 'stamp' | 'text';
    text: string | null;
    fontSize: number | null;
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    signedAt: string | null;
    declinedAt: string | null;
    signOrder: number | null;
    signer: { id: string; name: string; email: string; hasSignature: boolean } | null;
  }>;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DocStats {
  signedCount: number;
  pagesSaved: number;
  mvrSaved: number;
  mvrPerPage: number;
}

export const documentsApi = {
  /** Org-wide impact figures for the dashboard (documents signed + MVR/paper saved). */
  stats: () => api.get<DocStats>('/documents/stats').then((r) => r.data),
  list: (tab: DocTab, page = 1, pageSize = 20, q?: string) =>
    api.get<{ documents: DocRow[]; total: number; page: number; pageSize: number }>(
      '/documents',
      { params: { tab, page, pageSize, ...(q ? { q } : {}) } },
    ).then((r) => ({ items: r.data.documents, total: r.data.total, page: r.data.page, pageSize: r.data.pageSize })),
  upload: (file: File, title: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    return api.post<{ document: { id: string; title: string; status: string; createdAt: string } }>('/documents', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  /** Batch upload: many PDFs, each becomes its own DRAFT. Returns created drafts + any per-file failures. */
  uploadBatch: (files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    return api.post<{
      documents: Array<{ id: string; title: string; status: string; createdAt: string }>;
      failed: Array<{ filename: string; error: string }>;
    }>('/documents/batch', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  get: (id: string) => api.get<{ document: DocDetail; role: 'sender' | 'signer' | 'reviewer' | 'admin' }>(`/documents/${id}`).then((r) => r.data),
  /** Reviewer (or org admin) approves or rejects an IN_REVIEW document. */
  review: (id: string, decision: 'APPROVED' | 'REJECTED', comment?: string) =>
    api.post<{ document: { id: string; status: string } }>(`/documents/${id}/review`, { decision, ...(comment ? { comment } : {}) }).then((r) => r.data),
  savePlaceholders: (
    id: string,
    items: Array<{ signerId?: string; kind?: 'signature' | 'date' | 'stamp' | 'text'; text?: string; fontSize?: number; page: number; x: number; y: number; width: number; height: number; signOrder?: number }>,
  ) => api.post<{ placeholders: DocDetail['placeholders'] }>(`/documents/${id}/placeholders`, { items }).then((r) => r.data),
  send: (id: string) => api.post<{ document: { id: string; status: string; sentAt: string } }>(`/documents/${id}/send`).then((r) => r.data),
  cancel: (id: string) => api.post<void>(`/documents/${id}/cancel`).then((r) => r.data),
  /** Permanently delete the document (DB row + all files, including the signed PDF). */
  remove: (id: string) => api.delete<void>(`/documents/${id}`).then((r) => r.data),
  /** Returns the public sign URL for the authenticated user if they're a pending signer. */
  mySignLink: (id: string) =>
    api.get<{ token: string; url: string }>(`/documents/${id}/my-sign-link`).then((r) => r.data),
  /** Path under /api — only useful for embedding in same-origin fetch via this module's `download` helper. */
  downloadUrl: (id: string, type: 'original' | 'signed') => `/api/documents/${id}/download?type=${type}`,
  autoDetect: (id: string) =>
    api.post<{
      detected: boolean;
      templateType: 'PV' | 'PO' | null;
      signers: Array<{
        order: number; roleLabel: string; printedName: string;
        matchedUserId: string | null; matchStatus: 'matched' | 'unmatched' | 'ambiguous';
        placeholders: Array<{ kind: 'signature' | 'date'; page: number; x: number; y: number; width: number; height: number; signOrder: number }>;
      }>;
    }>(`/documents/${id}/auto-detect`).then((r) => r.data),
  /** PV only: auto-sign with each signatory's saved signature + org stamp and complete (no send). */
  autoSignPv: (id: string) =>
    api.post<{ queued: boolean; signedCount: number; skippedSigners: string[] }>(`/documents/${id}/auto-sign-pv`).then((r) => r.data),
  /** Batch: detect PV/PO, auto-place boxes for matched signatories + stamp, persist, and auto-sign — server-side. */
  autoPlaceAndSign: (id: string) =>
    api.post<{ queued: boolean; signedCount: number; skippedSigners: string[]; templateType: 'PV' | 'PO'; unmatched: string[] }>(
      `/documents/${id}/auto-place-and-sign`,
    ).then((r) => r.data),
  /** Download the signed PDFs for several documents as one ZIP. */
  downloadZip: async (ids: string[], filename = 'signed-documents.zip'): Promise<void> => {
    const r = await api.post<Blob>('/documents/download-zip', { ids }, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },
  /**
   * Triggers a browser download by fetching the PDF through axios (so the Bearer token is attached),
   * converting the response to a Blob URL, and clicking a synthetic anchor.
   * Plain <a href> doesn't work because the auth header is never sent.
   */
  download: async (id: string, type: 'original' | 'signed', filename: string): Promise<void> => {
    const r = await api.get<Blob>(`/documents/${id}/download`, { params: { type }, responseType: 'blob' });
    const blobUrl = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },
};
